import { useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { forceSignOut } from '@/lib/forceSignOut';

const DEFAULT_IDLE_MINUTES = 180; // 3 timer fallback
const WARNING_MS = 2 * 60 * 1000; // 2 min advarsel før logout
const HEARTBEAT_MS = 60 * 1000;
const ACTIVITY_THROTTLE_MS = 5000;
const STORAGE_KEY = 'finiq_last_activity';

const ACTIVITY_EVENTS: (keyof WindowEventMap)[] = [
  'mousemove', 'mousedown', 'keydown', 'click', 'touchstart', 'scroll', 'wheel',
];

export interface IdleLogoutState {
  showWarning: boolean;
  secondsUntilLogout: number;
  stayLoggedIn: () => void;
  logoutNow: () => void;
  effectiveTimeoutMinutes: number;
  lastActivityAt: number;
}

export function useIdleLogout(): IdleLogoutState {
  const { user } = useAuth();
  const [showWarning, setShowWarning] = useState(false);
  const [secondsUntilLogout, setSecondsUntilLogout] = useState(120);
  const [effectiveTimeoutMinutes, setEffectiveTimeoutMinutes] = useState(DEFAULT_IDLE_MINUTES);
  const [lastActivityAt, setLastActivityAt] = useState(() => Date.now());
  const lastActivityRef = useRef<number>(Date.now());
  const lastWriteRef = useRef<number>(0);
  const warningTimerRef = useRef<number | null>(null);
  const logoutTimerRef = useRef<number | null>(null);
  const countdownRef = useRef<number | null>(null);
  const timeoutMsRef = useRef<number>(DEFAULT_IDLE_MINUTES * 60 * 1000);

  const clearTimers = useCallback(() => {
    if (warningTimerRef.current) window.clearTimeout(warningTimerRef.current);
    if (logoutTimerRef.current) window.clearTimeout(logoutTimerRef.current);
    if (countdownRef.current) window.clearInterval(countdownRef.current);
    warningTimerRef.current = null;
    logoutTimerRef.current = null;
    countdownRef.current = null;
  }, []);

  const scheduleTimers = useCallback(() => {
    clearTimers();
    const idleMs = timeoutMsRef.current;
    warningTimerRef.current = window.setTimeout(() => {
      setSecondsUntilLogout(Math.floor(WARNING_MS / 1000));
      setShowWarning(true);
      countdownRef.current = window.setInterval(() => {
        setSecondsUntilLogout((s) => (s > 0 ? s - 1 : 0));
      }, 1000);
    }, Math.max(0, idleMs - WARNING_MS));

    logoutTimerRef.current = window.setTimeout(async () => {
      setShowWarning(false);
      clearTimers();
      await forceSignOut('idle_timeout', {
        timeoutMinutes: Math.round(idleMs / 60000),
        lastActivityAt: lastActivityRef.current,
      });
    }, idleMs);
  }, [clearTimers]);

  const recordActivity = useCallback(
    (broadcast = true) => {
      const now = Date.now();
      lastActivityRef.current = now;
      setLastActivityAt(now);
      if (broadcast && now - lastWriteRef.current > ACTIVITY_THROTTLE_MS) {
        lastWriteRef.current = now;
        try { localStorage.setItem(STORAGE_KEY, String(now)); } catch { /* noop */ }
      }
      if (showWarning) setShowWarning(false);
      scheduleTimers();
    },
    [scheduleTimers, showWarning],
  );

  const stayLoggedIn = useCallback(() => recordActivity(true), [recordActivity]);

  const logoutNow = useCallback(() => {
    clearTimers();
    setShowWarning(false);
    void forceSignOut('manual', {
      timeoutMinutes: Math.round(timeoutMsRef.current / 60000),
      lastActivityAt: lastActivityRef.current,
    });
  }, [clearTimers]);

  // Load effective timeout from DB per user
  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase.rpc('get_effective_idle_timeout', { _user_id: user.id });
        if (cancelled) return;
        const mins = typeof data === 'number' && data > 0 ? data : DEFAULT_IDLE_MINUTES;
        setEffectiveTimeoutMinutes(mins);
        timeoutMsRef.current = mins * 60 * 1000;
        scheduleTimers();
      } catch {
        timeoutMsRef.current = DEFAULT_IDLE_MINUTES * 60 * 1000;
        scheduleTimers();
      }
    })();
    return () => { cancelled = true; };
  }, [user?.id, scheduleTimers]);

  // Setup listeners + heartbeat — only when authenticated
  useEffect(() => {
    if (!user?.id) {
      clearTimers();
      setShowWarning(false);
      return;
    }

    recordActivity(true);

    const handleActivity = () => recordActivity(true);
    ACTIVITY_EVENTS.forEach((evt) => window.addEventListener(evt, handleActivity, { passive: true }));

    const handleStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY && e.newValue) {
        const ts = Number(e.newValue);
        if (!Number.isNaN(ts) && ts > lastActivityRef.current) {
          lastActivityRef.current = ts;
          setLastActivityAt(ts);
          if (showWarning) setShowWarning(false);
          scheduleTimers();
        }
      }
    };
    window.addEventListener('storage', handleStorage);

    const heartbeat = window.setInterval(async () => {
      const sinceActive = Date.now() - lastActivityRef.current;
      if (sinceActive < HEARTBEAT_MS * 2) {
        try {
          await supabase.from('profiles').update({ last_active_at: new Date().toISOString() }).eq('id', user.id);
        } catch { /* silent */ }
      }
    }, HEARTBEAT_MS);

    void supabase.from('profiles').update({ last_active_at: new Date().toISOString() }).eq('id', user.id);

    return () => {
      ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, handleActivity));
      window.removeEventListener('storage', handleStorage);
      window.clearInterval(heartbeat);
      clearTimers();
    };
  }, [user?.id, recordActivity, scheduleTimers, clearTimers, showWarning]);

  return { showWarning, secondsUntilLogout, stayLoggedIn, logoutNow, effectiveTimeoutMinutes, lastActivityAt };
}
